import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCheck, Clock } from 'lucide-react';

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  white: '#FFFFFF',
  darkText: '#1a365d'
};

const TeamModal = ({ isOpen, onClose, initialData }) => {
  const [teamData, setTeamData] = useState({
    bgColor: '#f8fafc',
    textColor: '#1a365d',
    teamInfo: {
      headings: {
        title: "",
        subheading: ""
      },
      members: []
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(null);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/about/team/`);
          if (response.ok) {
            const data = await response.json();
            setTeamData(data);
          }
        } catch (error) {
          console.error('Error fetching team data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setTeamData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeamData(prev => ({ ...prev, [name]: value }));
  };

  const handleHeadingChange = (field, value) => {
    setTeamData(prev => ({
      ...prev,
      teamInfo: {
        ...prev.teamInfo,
        headings: {
          ...prev.teamInfo.headings,
          [field]: value
        }
      }
    }));
  };

  const handleMemberChange = (index, field, value) => {
    const updatedMembers = [...teamData.teamInfo.members];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    setTeamData(prev => ({
      ...prev,
      teamInfo: {
        ...prev.teamInfo,
        members: updatedMembers
      }
    }));
  };

  const addMember = () => {
    setTeamData(prev => ({
      ...prev,
      teamInfo: {
        ...prev.teamInfo,
        members: [
          ...prev.teamInfo.members,
          {
            name: "",
            position: "",
            image: "",
            expertise: "",
            years: ""
          }
        ]
      }
    }));
  };

  const removeMember = (index) => {
    const updatedMembers = [...teamData.teamInfo.members];
    updatedMembers.splice(index, 1);
    setTeamData(prev => ({
      ...prev,
      teamInfo: {
        ...prev.teamInfo,
        members: updatedMembers
      }
    }));
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'team_members');

    try {
      setIsUploading(true);
      setCurrentUploadIndex(index);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        handleMemberChange(index, 'image', imageUrl);
      } else {
        throw new Error('Invalid image URL in response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setCurrentUploadIndex(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/about/team/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teamData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update team data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating team data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading team data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-6xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Team Section</h3>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Background Color</span>
              </label>
              <input 
                type="color" 
                value={teamData.bgColor || '#f8fafc'} 
                onChange={(e) => handleChange('bgColor', e.target.value)} 
                className="input input-bordered w-full" 
              />
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Text Color</span>
              </label>
              <input 
                type="color" 
                value={teamData.textColor || '#1a365d'} 
                onChange={(e) => handleChange('textColor', e.target.value)} 
                className="input input-bordered w-full" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Section Title</span>
              </label>
              <input 
                type="text" 
                value={teamData.teamInfo?.headings?.title || ''} 
                onChange={(e) => handleHeadingChange('title', e.target.value)} 
                className="input input-bordered w-full" 
              />
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Section Subheading</span>
              </label>
              <input 
                type="text" 
                value={teamData.teamInfo?.headings?.subheading || ''} 
                onChange={(e) => handleHeadingChange('subheading', e.target.value)} 
                className="input input-bordered w-full" 
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-bold">Team Members ({teamData.teamInfo?.members?.length || 0})</h4>
              <button onClick={addMember} className="btn btn-sm btn-primary">
                Add Member
              </button>
            </div>
            
            {teamData.teamInfo?.members?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No team members added yet. Click "Add Member" to create one.
              </div>
            ) : (
              teamData.teamInfo?.members?.map((member, index) => (
                <div key={index} className="card bg-base-100 shadow-sm p-4 relative">
                  <button 
                    onClick={() => removeMember(index)}
                    className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                  >
                    ✕
                  </button>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Member Name</span>
                      </label>
                      <input 
                        type="text" 
                        value={member.name || ''} 
                        onChange={(e) => handleMemberChange(index, 'name', e.target.value)} 
                        className="input input-bordered w-full" 
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Position</span>
                      </label>
                      <input 
                        type="text" 
                        value={member.position || ''} 
                        onChange={(e) => handleMemberChange(index, 'position', e.target.value)} 
                        className="input input-bordered w-full" 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Expertise</span>
                      </label>
                      <input 
                        type="text" 
                        value={member.expertise || ''} 
                        onChange={(e) => handleMemberChange(index, 'expertise', e.target.value)} 
                        className="input input-bordered w-full" 
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Experience</span>
                      </label>
                      <input 
                        type="text" 
                        value={member.years || ''} 
                        onChange={(e) => handleMemberChange(index, 'years', e.target.value)} 
                        className="input input-bordered w-full" 
                      />
                    </div>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Member Photo</span>
                    </label>
                    <div className="flex items-center gap-2">
                      {member.image && (
                        <div className="avatar">
                          <div className="w-12 rounded">
                            <img src={member.image} alt="Member preview" />
                          </div>
                        </div>
                      )}
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, index)}
                        className="file-input file-input-bordered w-full max-w-xs"
                        disabled={isUploading && currentUploadIndex === index}
                      />
                      {isUploading && currentUploadIndex === index && (
                        <progress 
                          className="progress progress-primary w-20" 
                          value={uploadProgress} 
                          max="100"
                        ></progress>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default TeamModal;